import React, { useEffect, useState } from 'react';
import { Control, Controller, ControllerFieldState, ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { FormField, Input } from '@legalshield/adonis-ux-framework';

import { CurrencyAmount } from '../types';

const parseToCents = (raw: string): number => {
  const cleaned = (raw ?? '').replace(/[$,\s]/g, '');
  if (!cleaned) return 0;
  const num = Number(cleaned);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.round(num * 100);
};

const formatDisplay = (cents: number): string => {
  if (!cents || cents < 0) return '';
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
};

type InnerProps<T extends FieldValues> = {
  field: ControllerRenderProps<T, Path<T>>;
  fieldState: ControllerFieldState;
  name: Path<T>;
  label: string;
  required?: boolean;
};

const CurrencyInner = <T extends FieldValues>({ field, fieldState, name, label, required }: InnerProps<T>) => {
  const current = (field.value as CurrencyAmount | undefined) ?? { display: '', value: 0 };
  const [editing, setEditing] = useState<string>(current.display ?? '');

  useEffect(() => {
    setEditing(current.display ?? '');
  }, [current.display]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setEditing(raw);
    field.onChange({ display: raw, value: parseToCents(raw) });
  };

  const handleBlur = () => {
    const cents = parseToCents(editing);
    if (cents > 0) {
      const normalized = formatDisplay(cents);
      setEditing(normalized);
      field.onChange({ display: normalized, value: cents });
    }
    field.onBlur();
  };

  const status = fieldState.error ? 'invalid' : undefined;

  return (
    <FormField
      id={String(name)}
      label={label}
      required={required}
      status={status}
      validationHint={fieldState.error?.message}
    >
      <Input
        placeholder={label}
        type="text"
        value={editing}
        onChange={handleChange}
        onBlur={handleBlur}
        status={status}
      />
    </FormField>
  );
};

const CurrencyField = <T extends FieldValues>({ control, name, label, required }: Props<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <CurrencyInner field={field} fieldState={fieldState} name={name} label={label} required={required} />
    )}
  />
);

export default CurrencyField;
